import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import AnnouncementBar from "@/components/AnnouncementBar";
import TrustStrip from "@/components/TrustStrip";
import PricingSection from "@/components/PricingSection";
import SocialProofStrip from "@/components/SocialProofStrip";
import FaqSection from "@/components/FaqSection";
import { BrandLogo } from "@/components/BrandLogo";
import { trackEvent } from "@/lib/analytics";
import { usePublicSettings } from "@/lib/siteSettings";
import { renderRich } from "@/lib/richText";
import { HOMEPAGE_DEFAULTS } from "@/lib/homepageDefaults";
import {
  Brain,
  Target,
  Zap,
  ChevronRight,
  Sparkles,
  BookOpen,
  Clock,
  MessageCircle,
  CheckCircle2,
  XCircle,
  Briefcase,
  ShieldCheck,
  Mic,
} from "lucide-react";

const features = [
  {
    icon: Target,
    title: "1,200+ Exam-Style MCQs - Part 1's Exact Format",
    description:
      "Original scenario-based questions aligned to the current ADC written examination blueprint and Australian clinical guidance. Per-option explanations on 1,000+ so you understand why the answer is right.",
    accent: "text-primary",
  },
  {
    icon: ShieldCheck,
    title: "Live Readiness Score + Pass Prediction",
    description:
      "3 full ADC-style readiness trials (140 questions, timed) feed a live pass-probability gauge. Know if you're truly ready - instead of guessing the night before you book the $2,122 exam.",
    accent: "text-accent",
  },
  {
    icon: Brain,
    title: "Adaptive - Focuses on YOUR Weak Areas",
    description:
      "Our engine spots your gaps and feeds you exactly the right questions. Not the same generic plan for everyone - your daily practice targets what's actually costing you marks.",
    accent: "text-success",
  },
  {
    icon: MessageCircle,
    title: "Smart Tutor - 24/7",
    description:
      "Stuck on a scenario at 2am? Tap 'Ask tutor' and get a clear, plain-English explanation in seconds - not next Tuesday's Zoom call.",
    accent: "text-primary",
  },
  {
    icon: Zap,
    title: "Start Today. No Cohorts.",
    description:
      "No April or October enrolment dates. No waiting. Hit the button and start studying in 30 seconds - on your timeline, at your pace.",
    accent: "text-warning",
  },
  {
    icon: Briefcase,
    title: "Bonus: Part 2 Prep Included",
    description:
      "33 real OSCE cases + voice patient simulator (Dry Socket, Cellulitis, Trauma, Consent). Pass Part 1, then keep going with Part 2 already built for you.",
    accent: "text-warning",
  },
];

const subjects = [
  "Oral Surgery", "Orthodontics", "Periodontics", "Pharmacology",
  "Prosthodontics", "Endodontics", "Operative", "Oral Medicine",
  "Pediatric", "Community",
];

const LandingPage = () => {
  const navigate = useNavigate();
  const settings = usePublicSettings();
  const hero = { ...HOMEPAGE_DEFAULTS.hero, ...(settings["homepage.hero"] || {}) };
  const sits = { ...HOMEPAGE_DEFAULTS.sits_on_top, ...(settings["homepage.sits_on_top"] || {}) };
  const comparison = { ...HOMEPAGE_DEFAULTS.comparison, ...(settings["homepage.comparison"] || {}) };
  const commitment = { ...HOMEPAGE_DEFAULTS.commitment, ...(settings["homepage.commitment"] || {}) };
  const finalCta = { ...HOMEPAGE_DEFAULTS.final_cta, ...(settings["homepage.final_cta"] || {}) };
  const testimonials = settings["homepage.testimonials"] || { show_section: true, heading: "", subtitle: "", items: [] };
  const visibleTestimonials = (testimonials.items || []).filter((t) => t.show === true && t.consent_date);
  const trust = settings["homepage.trust_strip"] || { show_section: true, cards: [] };

  const goCta = (url, locName) => {
    trackEvent("readiness_cta_clicked", { location: locName });
    if (url && url.startsWith("/")) navigate(url);
    else if (url) window.location.href = url;
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <AnnouncementBar context="public" />
      {/* Gradient overlay */}
      <div className="absolute inset-0 gradient-hero pointer-events-none" />

      {/* Top nav - logo + sign in for returning users */}
      <header className="relative z-10 px-6 pt-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandLogo size={24} />
            <span className="font-bold text-base">PassADC</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full text-sm font-medium hover:bg-secondary"
            onClick={() => navigate("/login")}
            data-testid="nav-sign-in-btn"
          >
            Sign in
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-10 md:pt-16 pb-16 md:pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-center mb-6 animate-fade-in-up">
            <div className="glass px-4 py-2 rounded-full border border-primary/30 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-xs md:text-sm">
                The practice + readiness system for ADC Part 1 (Written Examination)
              </span>
            </div>
          </div>

          <h1
            data-testid="landing-headline"
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-center mb-5 animate-fade-in-up stagger-1 leading-tight max-w-3xl mx-auto"
          >
            {renderRich(hero.headline, "text-primary")}
          </h1>

          <p className="text-base md:text-xl text-muted-foreground text-center max-w-2xl mx-auto mb-8 animate-fade-in-up stagger-2">
            {renderRich(hero.subheadline, "text-foreground")}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center animate-fade-in-up stagger-3">
            <Button
              data-testid="hero-readiness-check-btn"
              size="lg"
              className="glow-primary rounded-full px-8 py-6 text-base md:text-lg font-semibold w-full sm:w-auto min-w-[280px]"
              onClick={() => goCta(hero.button_url, "hero_primary")}
            >
              {hero.button_label}
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center mt-4">
            {hero.subline}
          </p>

          <div className="mt-5 flex justify-center">
            <Link to="/guarantee" data-testid="hero-guarantee-badge"
                  className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/5 px-4 py-2 text-xs sm:text-sm font-semibold text-primary hover:bg-primary/10 transition">
              <ShieldCheck className="w-4 h-4" />
              Backed by the 80% Readiness Commitment
            </Link>
          </div>

          <SocialProofStrip />

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto mt-12 md:mt-16 animate-fade-in-up stagger-4">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary mb-1">1,200+</div>
              <div className="text-xs text-muted-foreground">Exam-style MCQs</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-accent mb-1">33</div>
              <div className="text-xs text-muted-foreground">OSCE clinical cases</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-success mb-1">24/7</div>
              <div className="text-xs text-muted-foreground">Personal tutor on call</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary mb-1">10</div>
              <div className="text-xs text-muted-foreground">Subjects covered</div>
            </div>
          </div>
        </div>
      </section>

      {/* Credibility strip — independence disclaimer + trust signals */}
      {sits.show !== false && (
      <section className="relative py-10 px-6 border-y border-border bg-card/60" data-testid="credibility-strip">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div className="rounded-2xl overflow-hidden aspect-[4/3] md:aspect-[3/2] md:col-span-1">
            <img
              src="https://images.unsplash.com/photo-1606811971618-4486d14f3f99?w=900&q=80&auto=format&fit=crop"
              alt="Internationally qualified dentist reviewing clinical notes"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="md:col-span-2 space-y-3">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-semibold">
              <ShieldCheck className="w-3.5 h-3.5" /> {sits.badge}
            </div>
            <h2 className="text-xl md:text-2xl font-bold leading-tight">
              {renderRich(sits.heading, "text-primary")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {renderRich(sits.body, "text-foreground")}
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-foreground/90 pt-1">
              {(sits.bullets || []).map((b, i) => (
                <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-success mt-0.5 shrink-0" /> {b}</li>
              ))}
            </ul>
            <p className="text-[11px] text-muted-foreground pt-2 border-t border-border">
              {sits.disclaimer}
            </p>
          </div>
        </div>
      </section>
      )}

      {/* Start Preparing Today for $0 - free-value block directly after hero */}
      <section className="py-16 md:py-20 px-6 border-y border-border bg-primary/[0.03]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-bold mb-3">
            Start with <span className="text-primary">7 days of full Pro.</span> Free.
          </h2>
          <p className="text-muted-foreground text-sm md:text-base mb-8 max-w-xl mx-auto">
            Every new account unlocks everything for 7 days - no credit card. After that, keep studying free with the essentials.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left text-sm max-w-xl mx-auto mb-8">
            {[
              "7 days: full 1,200+ question bank",
              "7 days: unlimited AI tutor",
              "7 days: full mock exams + weakness analysis",
              "Free forever: 10 MCQs every day",
              "Free forever: daily practice questions and preview lessons in every ADC subject",
              "Free forever: member community",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2 p-3 rounded-xl bg-card border border-border">
                <CheckCircle2 className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <Button
            data-testid="free-value-signup-btn"
            size="lg"
            className="rounded-full px-8 py-6 text-base md:text-lg font-semibold"
            onClick={() => {
              trackEvent("readiness_cta_clicked", { location: "free_value_block" });
              navigate("/signup");
            }}
          >
            Start My Free 7-Day Pro Trial
            <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
          <p className="text-xs text-muted-foreground mt-3">
            $0 today · No credit card required · Free plan after 7 days
          </p>
        </div>
      </section>

      {/* Simple 3-step process */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">
            Stop guessing. <span className="text-primary">Start with a plan.</span>
          </h2>
          <p className="text-muted-foreground text-sm md:text-base mb-14 max-w-lg mx-auto">
            Three steps between where you are and knowing exactly what to study next.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            {[
              { n: 1, title: "Check where you stand", desc: "Take the quick readiness check and identify possible weak areas." },
              { n: 2, title: "Study what needs work", desc: "PassADC guides your learning and practice around your weaker subjects." },
              { n: 3, title: "Track your progress", desc: "Practise, take full readiness trials and watch your readiness improve." },
            ].map((step) => (
              <div key={step.n} className="glass rounded-2xl border border-border p-6">
                <div className="w-10 h-10 rounded-full bg-primary/15 text-primary text-lg font-bold flex items-center justify-center mb-4">
                  {step.n}
                </div>
                <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.desc}</p>
              </div>
            ))}
          </div>
          <Button
            data-testid="three-step-cta-btn"
            size="lg"
            className="rounded-full px-8 py-6 text-base md:text-lg font-semibold mt-12 glow-primary"
            onClick={() => {
              trackEvent("readiness_cta_clicked", { location: "three_step_section" });
              navigate("/readiness-check");
            }}
          >
            Check My Readiness Free
            <ChevronRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Subject Pills */}
      <section className="py-12 border-y border-border">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs text-center text-muted-foreground uppercase tracking-wider mb-5">
            All ADC Part 1 subjects covered
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {subjects.map((subject) => (
              <div
                key={subject}
                className="glass px-4 py-2 rounded-full border border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/50 transition-all cursor-default"
              >
                {subject}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">
            Built differently. <span className="text-primary">On purpose.</span>
          </h2>
          <p className="text-muted-foreground text-center mb-16 max-w-xl mx-auto">
            We replaced expensive cohort coaching with structured, self-paced prep - on your schedule.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="group relative overflow-hidden rounded-2xl border border-border bg-gradient-to-b from-white/5 to-transparent p-8 hover:border-primary/50 transition-all duration-500 hover-lift"
              >
                <div className="mb-4 p-3 rounded-xl bg-primary/10 w-fit">
                  <feature.icon className={`w-6 h-6 ${feature.accent}`} />
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>

                <div className="absolute inset-0 bg-gradient-to-t from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      {comparison.show !== false && (
      <section className="py-24 px-6 border-y border-border bg-white/[0.015]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">
            {renderRich(comparison.heading, "text-muted-foreground")}
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-xl mx-auto text-sm">
            {comparison.subheading}
          </p>

          <div className="glass rounded-2xl border border-border overflow-hidden" data-testid="comparison-table">
            <div className="grid grid-cols-3 px-4 sm:px-6 py-4 border-b border-border bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
              <div></div>
              <div className="text-primary font-semibold">{comparison.col_us}</div>
              <div>{comparison.col_them}</div>
            </div>
            {(comparison.rows || []).map((row) => (
              <div
                key={row.label}
                className="grid grid-cols-3 px-4 sm:px-6 py-4 border-b border-border last:border-0 text-sm"
              >
                <div className="font-medium pr-2">{row.label}</div>
                <div className="flex items-start gap-2 text-foreground pr-2">
                  <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span>{row.us}</span>
                </div>
                <div className="flex items-start gap-2 text-muted-foreground">
                  <XCircle className="w-4 h-4 text-destructive/70 flex-shrink-0 mt-0.5" />
                  <span>{row.them}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Personal tutor showcase */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-4">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-medium text-primary">Modern, structured prep</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Got a doubt? <span className="text-primary">Tap once.</span>
            </h2>
            <p className="text-muted-foreground mb-6">
              Every question has an <strong className="text-foreground">"Ask tutor: explain this in simple words"</strong> button.
              Get step-by-step explanations whenever you need them - in plain English, even at midnight, even on Sundays.
            </p>
            <ul className="space-y-3 text-sm">
              {[
                "Tailored to the exact question you got wrong",
                "Short sentences. Bullet points. ESL-friendly.",
                "Follow-up questions: 'but why not B?' answered in seconds",
              ].map((point) => (
                <li key={point} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Mock chat preview */}
          <div className="glass rounded-2xl border border-border p-6">
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-secondary flex-shrink-0" />
                <div className="flex-1 p-3 rounded-xl bg-secondary text-sm">
                  Why is the answer to that pulpitis question "irreversible"? I picked reversible.
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 p-3 rounded-xl bg-primary/10 border border-primary/20 text-sm space-y-2">
                  <p>Quick check: <strong className="text-primary">spontaneous pain</strong> + <strong className="text-primary">lingers &gt;15 sec</strong> = irreversible.</p>
                  <p>Reversible pulpitis pain stops as soon as the cold goes away. Your patient's pain didn't stop - that's the clue.</p>
                  <p className="text-xs text-muted-foreground italic">Exam tip: Lingering = irreversible. Always.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 px-6 border-t border-border">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-16">
            How it <span className="text-primary">works</span>
          </h2>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { n: 1, title: "Diagnose", desc: "Readiness check - find your weak topics", color: "text-primary", bg: "bg-primary/20" },
              { n: 2, title: "Learn", desc: "Read short notes by topic", color: "text-warning", bg: "bg-warning/20" },
              { n: 3, title: "Practice", desc: "MCQs + OSCE cases - adaptive", color: "text-success", bg: "bg-success/20" },
              { n: 4, title: "Mock Exam", desc: "Real exam mode. Repeat until ready.", color: "text-accent", bg: "bg-accent/20" },
            ].map((step) => (
              <div key={step.n} className="text-center">
                <div className={`w-16 h-16 rounded-full ${step.bg} flex items-center justify-center mx-auto mb-5`}>
                  <span className={`text-2xl font-bold ${step.color}`}>{step.n}</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <PricingSection />

      {/* Trust strip (admin-editable) - shown where testimonials were */}
      <TrustStrip data={trust} />

      {/* Social Proof - Testimonials (only real, consented, shown) */}
      {testimonials.show_section !== false && visibleTestimonials.length > 0 && (
      <section className="py-24 px-6 border-t border-border" data-testid="testimonials-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">
            {testimonials.heading}
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-xl mx-auto text-sm">
            {testimonials.subtitle}
          </p>

          <div className="grid md:grid-cols-3 gap-5">
            {visibleTestimonials.map((t, i) => (
              <div key={i} className="glass rounded-2xl p-6 border border-border flex flex-col" data-testid={`testimonial-${i}`}>
                <p className="text-sm leading-relaxed mb-5 flex-1">"{t.quote}"</p>
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div>
                    <p className="text-sm font-medium">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.country}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Pass Readiness Commitment */}
      {commitment.show !== false && (
      <section className="py-20 px-6 border-t border-border bg-primary/[0.02]">
        <div className="max-w-3xl mx-auto">
          <div className="glass-strong rounded-3xl p-8 md:p-10 border border-success/30 text-center">
            <div className="w-16 h-16 rounded-2xl bg-success/15 flex items-center justify-center mx-auto mb-5">
              <ShieldCheck className="w-8 h-8 text-success" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-3">
              {renderRich(commitment.heading, "text-success")}
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              {renderRich(commitment.body, "text-foreground")}
            </p>
            <div className="grid md:grid-cols-3 gap-4 text-sm max-w-2xl mx-auto">
              {(commitment.steps || []).map((s, i) => (
                <div key={i} className="p-4 rounded-xl bg-secondary border border-border">
                  <div className="w-7 h-7 rounded-full bg-success/20 text-success text-xs font-bold flex items-center justify-center mx-auto mb-2">
                    {i + 1}
                  </div>
                  <p className="text-sm font-medium mb-1">{s.t}</p>
                  <p className="text-xs text-muted-foreground">{s.d}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-6 italic">
              {commitment.footnote}
            </p>
          </div>
        </div>
      </section>
      )}

      {/* FAQ (matches FAQPage JSON-LD) */}
      <FaqSection />

      {/* Final CTA */}
      {finalCta.show !== false && (
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="glass-strong rounded-3xl p-12 border border-primary/20">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              {renderRich(finalCta.heading, "text-primary")}
            </h2>
            <p className="text-muted-foreground mb-8">
              {finalCta.body}
            </p>
            <Button
              data-testid="cta-start-now-btn"
              size="lg"
              className="glow-primary rounded-full px-10 py-6 text-lg font-semibold"
              onClick={() => goCta(finalCta.button_url, "final_cta")}
            >
              {finalCta.button_label}
              <Sparkles className="ml-2 w-5 h-5" />
            </Button>
            <p className="text-xs text-muted-foreground mt-4">
              {finalCta.subline}
            </p>
          </div>
        </div>
      </section>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage;
